import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, MagnifyingGlassPlus, ShoppingBag, X } from "@phosphor-icons/react";
import { addToCart, cartCount } from "@/lib/cart";
import ImageLightbox from "@/pages/shop/ImageLightbox";
import ColorSizePicker from "@/pages/shop/ColorSizePicker";
import SizeGuideModal from "@/pages/shop/SizeGuideModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-screen product-detail modal opened from a ProductCard. Handles variant
 * selection (color + size), thumbnail cycling, add-to-cart, and opens the
 * ImageLightbox for zoomed viewing. Extracted from Shop.jsx.
 */
export default function ProductModal({ productId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [pickerValid, setPickerValid] = useState(false);
  const [pickerError, setPickerError] = useState("");
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setImgIdx(0);
    fetch(`${BACKEND}/api/shop/products/${productId}`)
      // A non-OK response now returns clean JSON like {"detail":"..."} (503
      // when Printful is down and we have no last-known-good copy). Without
      // the r.ok gate the frontend was treating that error body as a valid
      // product and rendering an empty "success" branch (blank modal with
      // just the Add-to-cart / Buy-now buttons). Reject on non-OK so the
      // catch below sets `detail:{error:true}` and the error UI shows.
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`http ${r.status}`))))
      .then((d) => {
        if (!alive) return;
        // Belt-and-suspenders: if the body somehow has no product identity,
        // treat it as an error so we don't render an empty shell.
        if (!d || (!d.id && !d.name)) {
          setDetail({ error: true });
          return;
        }
        setDetail(d);
        // Don't preselect a specific variant here — the ColorSizePicker will emit
        // one only when the customer has satisfied both color + size requirements.
        setSelectedVariant(null);
        // Start on the product's hero thumbnail (what the user just clicked),
        // not blindly on images[0] which may be a back/detail shot.
        if (d.thumbnail_url && Array.isArray(d.images)) {
          const heroIdx = d.images.indexOf(d.thumbnail_url);
          if (heroIdx >= 0) setImgIdx(heroIdx);
          else setImgIdx(0);
        } else {
          setImgIdx(0);
        }
      })
      .catch(() => alive && setDetail({ error: true }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [productId]);

  // When variant changes, if it has its own preview image, switch the hero to it
  useEffect(() => {
    if (!detail?.images || !selectedVariant?.preview_url) return;
    const idx = detail.images.indexOf(selectedVariant.preview_url);
    if (idx >= 0) setImgIdx(idx);
  }, [selectedVariant, detail]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="shop-modal"
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-3 md:p-8 overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0A0A0A] border-2 border-white/15 max-w-5xl w-full max-h-[95vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <p className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#B32027]">/ Product</p>
          <button
            onClick={onClose}
            data-testid="shop-modal-close"
            className="w-9 h-9 border border-white/20 hover:border-[#B32027] hover:text-[#B32027] flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center text-white/60 text-sm uppercase tracking-[0.3em]">Loading…</div>
        )}

        {!loading && detail && !detail.error && (
          <div className="grid md:grid-cols-2 gap-0">
            <div className="bg-[#141414]">
              {/* On mobile the full-viewport-width aspect-square image looked
                  huge (390 × 390 px on a 390 px viewport) and pushed everything
                  else off-screen. Give the mobile hero a fixed 55vh height so
                  the whole modal (image + title + variant picker) fits above
                  the fold on a phone. Desktop keeps the aspect-square layout. */}
              <div className="relative w-full h-[55vh] md:h-auto md:aspect-square overflow-hidden bg-white group/img">
                {detail.images?.[imgIdx] ? (
                  <>
                    <img
                      src={detail.images[imgIdx]}
                      alt={detail.name}
                      className="w-full h-full object-contain cursor-zoom-in"
                      data-testid="shop-modal-hero-image"
                      onClick={() => setLightboxOpen(true)}
                    />
                    <button
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      aria-label="Zoom image"
                      data-testid="shop-modal-zoom-btn"
                      className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#B32027] text-white border-2 border-white/20 hover:border-[#B32027] transition-colors backdrop-blur opacity-0 group-hover/img:opacity-100 focus:opacity-100"
                    >
                      <MagnifyingGlassPlus size={16} weight="bold" />
                    </button>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <ShoppingBag size={64} weight="thin" />
                  </div>
                )}
                {detail.images?.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setImgIdx((i) => (i - 1 + detail.images.length) % detail.images.length)}
                      data-testid="shop-modal-prev"
                      aria-label="Previous mockup"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 hover:bg-[#B32027] text-white border-2 border-white/20 hover:border-[#B32027] transition-colors backdrop-blur"
                    >
                      <ArrowLeft size={18} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setImgIdx((i) => (i + 1) % detail.images.length)}
                      data-testid="shop-modal-next"
                      aria-label="Next mockup"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 hover:bg-[#B32027] text-white border-2 border-white/20 hover:border-[#B32027] transition-colors backdrop-blur"
                    >
                      <ArrowRight size={18} weight="bold" />
                    </button>
                    <span className="absolute bottom-3 right-3 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white bg-black/70 border border-white/20">
                      {imgIdx + 1} / {detail.images.length}
                    </span>
                  </>
                )}
              </div>
              {detail.images?.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {detail.images.map((img, i) => (
                    <button
                      key={img}
                      onClick={() => setImgIdx(i)}
                      className={`w-16 h-16 flex-shrink-0 border-2 bg-white overflow-hidden ${
                        imgIdx === i ? "border-[#B32027]" : "border-white/10 hover:border-white/40"
                      } transition-colors`}
                    >
                      <img src={img} alt="" className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 md:p-8 flex flex-col gap-5">
              <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight leading-[0.95]">
                {detail.name}
              </h2>
              <p className="text-2xl font-bold text-[#B32027]" data-testid="shop-modal-price">
                {selectedVariant?.retail_price
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: selectedVariant.currency || "USD",
                    }).format(parseFloat(selectedVariant.retail_price))
                  : "—"}
              </p>

              {detail.description && (
                <p className="text-white/70 text-sm leading-relaxed whitespace-pre-line">
                  {detail.description}
                </p>
              )}

              {/* Color + Size picker (separate rows, replaces old flat chip list) */}
              <ColorSizePicker
                variants={detail.variants || []}
                value={selectedVariant}
                onChange={setSelectedVariant}
                onValidityChange={(ok) => { setPickerValid(ok); if (ok) setPickerError(""); }}
                onOpenSizeGuide={() => setSizeGuideOpen(true)}
              />

              <div className="mt-2 space-y-2">
                {pickerError && (
                  <p data-testid="picker-error" className="text-[#B32027] text-xs uppercase tracking-[0.2em] font-bold">
                    {pickerError}
                  </p>
                )}
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedVariant || !pickerValid) {
                        setPickerError("Please select a color and size.");
                        return;
                      }
                      addToCart({
                        product_id: detail.id,
                        sync_variant_id: selectedVariant.id,
                        variant_id: selectedVariant.variant_id,
                        quantity: 1,
                        item_retail_price: Number(selectedVariant.retail_price || 0),
                        product_name: detail.name,
                        variant_name: selectedVariant.name || `${selectedVariant.color || ""} ${selectedVariant.size || ""}`.trim(),
                        color: selectedVariant.color || "",
                        size: selectedVariant.size || "",
                        image: selectedVariant.preview_url || detail.thumbnail_url || "",
                      });
                      setAddedToCart(true);
                      setTimeout(() => setAddedToCart(false), 2500);
                    }}
                    data-testid="shop-modal-add-cart"
                    disabled={!pickerValid}
                    className={`inline-flex items-center justify-center gap-2 border-2 px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors ${
                      pickerValid ? "border-[#B32027] hover:bg-[#B32027]/20 text-white" : "border-white/15 text-white/40 cursor-not-allowed"
                    }`}
                  >
                    {addedToCart ? "Added ✓" : "Add to cart"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedVariant || !pickerValid) {
                        setPickerError("Please select a color and size.");
                        return;
                      }
                      const url = `/merch/checkout?product=${detail.id}&variant=${selectedVariant.id}`;
                      window.location.href = url;
                    }}
                    data-testid="shop-modal-buy"
                    disabled={!pickerValid}
                    className={`inline-flex items-center justify-center gap-2 px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] transition-colors whitespace-nowrap ${
                      pickerValid ? "bg-[#B32027] hover:bg-[#D42730] text-white" : "bg-white/10 text-white/40 cursor-not-allowed"
                    }`}
                  >
                    Buy now <ArrowUpRight size={14} weight="bold" />
                  </button>
                </div>
                {addedToCart && (
                  <Link to="/cart" data-testid="shop-modal-view-cart" className="block text-center text-[10px] uppercase tracking-[0.25em] font-bold text-[#B32027] hover:text-white pt-1">
                    View cart · {cartCount()} item{cartCount() === 1 ? "" : "s"} →
                  </Link>
                )}
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/45 text-center pt-1">
                  Auto-ships from our Printful studio. Net proceeds donated 50/50 to Tunnel to Towers + FDNY Fire Family Transport.
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && detail?.error && (
          <div className="p-10 text-center text-white/60">Could not load this product. Please try again.</div>
        )}
      </div>
      {lightboxOpen && detail?.images?.[imgIdx] && (
        <ImageLightbox
          src={detail.images[imgIdx]}
          alt={detail.name}
          onClose={() => setLightboxOpen(false)}
          onPrev={detail.images.length > 1 ? () => setImgIdx((i) => (i - 1 + detail.images.length) % detail.images.length) : null}
          onNext={detail.images.length > 1 ? () => setImgIdx((i) => (i + 1) % detail.images.length) : null}
          indexLabel={detail.images.length > 1 ? `${imgIdx + 1} / ${detail.images.length}` : null}
        />
      )}
      {sizeGuideOpen && detail && (
        <SizeGuideModal
          productId={detail.id}
          productName={detail.name}
          onClose={() => setSizeGuideOpen(false)}
        />
      )}
    </div>
  );
}
